import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "../../components/ui/button";
import { PexIcon } from "../../components/icons/PexIcon";
import { API_BASE } from "../../lib/api";
import { fetchMe } from "../../lib/auth";
import { useToastStore } from "../../lib/toast";
import { useMe } from "../../lib/useMe";
import { pickT, useT } from "../../i18n";
import { useLocaleStore } from "../../store/locale";
import {
  sellerStoresI18n,
  type SellerStoresStrings,
} from "../../i18n/seller/stores";
import { realStoreIntegrationsI18n } from "../../i18n/settings/real-store-integrations";
import { StoreConnectedCelebration } from "../../components/StoreConnectedCelebration";
import { ConnectedStoreCard } from "../seller/stores/ConnectedStoreCard";
import { ConnectedStoreGroup } from "../seller/stores/ConnectedStoreGroup";
import { EtsyExtensionPanel } from "../seller/stores/EtsyExtensionPanel";
import { PlatformComparisonSection } from "../seller/stores/PlatformComparisonSection";
import { StoreProviderCard } from "../seller/stores/StoreProviderCard";
import { useSellerTour } from "../onboarding/seller-tour-context";
import { completeSellerTourBeforeOAuth } from "../onboarding/seller-tour-navigation";
import {
  STORE_INTEGRATIONS_QUERY_KEY,
  fetchStoreIntegrations,
  type StoreRow,
} from "../seller/stores/store-integrations-query";
import {
  COMING_SOON_STORE_PROVIDERS,
  CONNECTABLE_STORE_PROVIDERS,
  getStoreProvider,
  type StoreProviderDefinition,
} from "../seller/stores/store-providers";

const ETSY_REDIRECT_URI = `${API_BASE}/etsy/oauth/callback`;
// Only the scopes PEXIFY uses (Etsy API terms: request what you use). Buyer
// email + address on receipts (email_r, address_r), reviews (feedback_r), the
// connected user/shop identity (profile_r), orders + tracking (transactions_*),
// listings incl. the v2 draft push (listings_*), shop setup — shipping and
// processing profiles (shops_*). Removed 2026-10-26: address_w billing_r cart_r
// cart_w favorites_r favorites_w profile_w recommend_r recommend_w. A new scope
// needs every store to reconnect once; dropping one does not.
const ETSY_SCOPE =
  "address_r email_r feedback_r listings_d listings_r listings_w profile_r " +
  "shops_r shops_w transactions_r transactions_w";
const AMAZON_REDIRECT_URI = `${API_BASE}/amazon/oauth/callback`;

const AMAZON_REGIONS = [
  { value: "na" },
  { value: "eu" },
  { value: "fe" },
] as const;

const BACKFILL_WINDOWS = [
  { value: "30d" },
  { value: "90d" },
  { value: "1y" },
  { value: "all" },
] as const;

type ActivationPreflight = {
  has_access_token: boolean;
  fulfillment_ecosystem_ok: boolean;
  mapped_sku_count: number;
  unmapped_pending_count: number;
  failed_intake_count: number;
  parked_event_count: number;
  blockers: string[];
  warnings: string[];
};

type OAuthResult = {
  provider: "etsy" | "shopify" | "amazon" | "ebay";
  value: string | null;
  storeId: string | null;
  reason: string | null;
};

function readOAuthResult(): OAuthResult | null {
  const params = new URLSearchParams(window.location.search);
  if (params.get("ebay")) {
    return {
      provider: "ebay",
      value: params.get("ebay"),
      storeId: params.get("store"),
      reason: params.get("reason"),
    };
  }
  if (params.get("amazon")) {
    return {
      provider: "amazon",
      value: params.get("amazon"),
      storeId: params.get("store"),
      reason: params.get("reason"),
    };
  }
  if (params.get("shopify")) {
    return {
      provider: "shopify",
      value: params.get("shopify"),
      storeId: params.get("store"),
      reason: params.get("reason"),
    };
  }
  if (params.get("etsy")) {
    return {
      provider: "etsy",
      value: params.get("etsy"),
      storeId: params.get("store"),
      reason: params.get("reason"),
    };
  }
  return null;
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`);
  if (!response.ok) throw new Error(`${path} ${response.status}`);
  return response.json() as Promise<T>;
}

function ConnectedStoreItem({
  store,
  provider,
  copy,
  onBackfillWindow,
  onBackfill,
  onConnectEtsy,
  connecting,
  syncing,
  onListProduct,
  onViewStore,
  onEditStore,
  onReconnect,
  canConnect,
  canDelete,
}: {
  store: StoreRow;
  provider: StoreProviderDefinition;
  copy: SellerStoresStrings;
  onBackfillWindow: (store: StoreRow, window: string) => void;
  onBackfill: (store: StoreRow) => void;
  onConnectEtsy: (store: StoreRow) => void;
  connecting: boolean;
  syncing: boolean;
  onListProduct: () => void;
  onViewStore: () => void;
  onEditStore: () => void;
  onReconnect: (store: StoreRow) => void;
  canConnect: boolean;
  canDelete: boolean;
}) {
  const push = useToastStore((state) => state.push);
  const queryClient = useQueryClient();
  const s = useT(realStoreIntegrationsI18n);
  const [preflight, setPreflight] = useState<ActivationPreflight | null>(null);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const connected = Boolean(store.has_access_token);
  const receivingOrders = connected && Boolean(store.order_intake_enabled);

  const intake = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await fetch(
        `${API_BASE}/store-integrations/${store.id}/order-intake`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ enabled }),
        },
      );
      const body = (await response.json().catch(() => null)) as {
        detail?: { message?: string; blockers?: string[] };
      } | null;
      if (!response.ok) {
        throw new Error(
          body?.detail?.message ??
            body?.detail?.blockers?.join(" · ") ??
            `order-intake ${response.status}`,
        );
      }
    },
    onSuccess: (_data, enabled) => {
      push({
        variant: "success",
        title: enabled ? s.toasts.intakeEnabled : s.toasts.intakeDisabled,
        duration_ms: 4000,
      });
      void queryClient.invalidateQueries({
        queryKey: STORE_INTEGRATIONS_QUERY_KEY,
      });
      setPreflight(null);
    },
    onError: (error: Error) =>
      push({
        variant: "error",
        title: s.toasts.intakeRefused,
        description: error.message,
        duration_ms: 7000,
      }),
  });

  const preflightCheck = useMutation({
    mutationFn: () =>
      getJson<ActivationPreflight>(
        `/store-integrations/${store.id}/activation-preflight`,
      ),
    onSuccess: setPreflight,
    onError: () =>
      push({
        variant: "error",
        title: s.toasts.preflightFailed,
        description: s.toasts.preflightFailedDescription,
        duration_ms: 5000,
      }),
  });

  // Remove a store that never finished connecting — an abandoned "Etsy store
  // (connecting…)" placeholder the reconnect flow left behind. Soft-delete via
  // the existing endpoint; only offered while the row has no access token, so a
  // live connected store can never be dropped from here.
  const remove = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `${API_BASE}/store-integrations/${store.id}`,
        { method: "DELETE" },
      );
      if (!response.ok && response.status !== 204) {
        const body = (await response.json().catch(() => null)) as {
          detail?: { message?: string };
        } | null;
        throw new Error(body?.detail?.message ?? `remove ${response.status}`);
      }
    },
    onSuccess: () => {
      setConfirmRemove(false);
      push({
        variant: "success",
        title: s.toasts.storeRemoved,
        duration_ms: 4000,
      });
      void queryClient.invalidateQueries({
        queryKey: STORE_INTEGRATIONS_QUERY_KEY,
      });
    },
    onError: (error: Error) =>
      push({
        variant: "error",
        title: s.toasts.removeFailed,
        description: error.message,
        duration_ms: 7000,
      }),
  });

  return (
    <ConnectedStoreCard
      copy={copy}
      store={{
        id: store.id,
        shopName: store.shop_name,
        provider,
        active: store.active !== false,
        connected,
        receivingOrders,
      }}
      lastSyncAt={store.last_sync_at}
      orderIntakePending={intake.isPending || preflightCheck.isPending}
      orderIntakeArming={preflight !== null}
      detailsAvailable
      detailsExpanded={detailsExpanded}
      onToggleDetails={() => setDetailsExpanded((current) => !current)}
      onToggleOrderIntake={() => {
        if (receivingOrders) {
          intake.mutate(false);
          return;
        }
        setDetailsExpanded(true);
        preflightCheck.mutate();
      }}
      onListProduct={onListProduct}
      onViewStore={onViewStore}
      onEditStore={onEditStore}
      onReconnect={canConnect ? () => onReconnect(store) : undefined}
      onDisconnect={canDelete ? () => remove.mutate() : undefined}
      disconnectPending={remove.isPending}
    >
      {!connected && provider.id === "etsy" ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[var(--color-text-muted)]">
            {s.completeEtsyAuth}
          </p>
          {confirmRemove && canDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-[var(--color-text-muted)]">
                {s.removeUnconnectedConfirm}
              </span>
              <Button
                variant="destructive"
                size="sm"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                {remove.isPending ? s.removing : s.remove}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={remove.isPending}
                onClick={() => setConfirmRemove(false)}
              >
                {s.cancel}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {canDelete ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmRemove(true)}
                >
                  {s.remove}
                </Button>
              ) : null}
              {canConnect ? (
                <Button
                  variant="primary"
                  size="sm"
                  data-tour="seller-connect-oauth"
                  disabled={connecting}
                  onClick={() => onConnectEtsy(store)}
                >
                  {connecting ? s.connecting : copy.actions.connect}
                </Button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
      {connected && store.provider === "etsy" ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--color-text-muted)]">
            <PexIcon name="activity" size="xs" tone="muted" />
            {s.importPastOrders}
          </span>
          <select
            aria-label={s.orderHistoryWindow}
            value={store.order_backfill_window ?? "90d"}
            onChange={(event) => onBackfillWindow(store, event.target.value)}
            className="min-h-[40px] rounded-[var(--radius-control)] border border-[var(--input-border)] bg-[var(--color-surface)] px-2 py-1.5 text-xs sm:min-h-[32px]"
          >
            {BACKFILL_WINDOWS.map((window) => (
              <option key={window.value} value={window.value}>
                {s.backfillWindows[window.value]}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            disabled={syncing}
            onClick={() => onBackfill(store)}
          >
            {syncing ? s.syncing : s.syncHistory}
          </Button>
        </div>
      ) : null}
      {connected && store.provider === "etsy" ? (
        <div className="mt-3 border-t border-[var(--color-border)] pt-3">
          <EtsyExtensionPanel storeId={store.id} copy={copy.extension} />
        </div>
      ) : null}
      {preflight ? (
        <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-xs text-[var(--color-text-muted)]">
          <p className="font-semibold text-slate-800">{s.preflight.title}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {s.preflight.hint}
          </p>
          <div className="mt-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-4">
            <span>
              {s.preflight.token}{" "}
              {preflight.has_access_token ? s.preflight.present : s.preflight.missing}
            </span>
            <span>
              {s.preflight.fulfillment}{" "}
              {preflight.fulfillment_ecosystem_ok ? s.preflight.ready : s.preflight.inactive}
            </span>
            <span>{s.preflight.mappedSkus} {preflight.mapped_sku_count}</span>
            <span>{s.preflight.parkedOrders} {preflight.parked_event_count}</span>
          </div>
          {preflight.blockers.length > 0 ? (
            <ul className="mt-2 list-disc rounded-md border border-red-200 bg-red-50 px-7 py-2 text-red-700">
              {preflight.blockers.map((blocker) => (
                <li key={blocker}>{blocker.replaceAll("_", " ")}</li>
              ))}
            </ul>
          ) : null}
          {preflight.warnings.length > 0 ? (
            <ul className="mt-2 list-disc rounded-md border border-amber-200 bg-amber-50 px-7 py-2 text-amber-800">
              {preflight.warnings.map((warning) => (
                <li key={warning}>{warning.replaceAll("_", " ")}</li>
              ))}
            </ul>
          ) : null}
          <div className="mt-3 flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPreflight(null)}
            >
              {s.cancel}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={intake.isPending || preflight.blockers.length > 0}
              onClick={() => intake.mutate(true)}
            >
              {s.preflight.confirmEnable}
            </Button>
          </div>
        </div>
      ) : null}
    </ConnectedStoreCard>
  );
}

export function RealStoreIntegrations() {
  const push = useToastStore((state) => state.push);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const t = useT(sellerStoresI18n);
  const s = useT(realStoreIntegrationsI18n);
  const sellerTour = useSellerTour();
  const { hasCapability } = useMe();
  const canConnect = hasCapability("store_integration.create");
  const canDelete = hasCapability("store_integration.delete");
  const [activeForm, setActiveForm] = useState<
    "etsy" | "shopify" | "amazon" | "ebay" | null
  >(null);
  const [shopDomain, setShopDomain] = useState("");
  const [amazonRegion, setAmazonRegion] = useState("na");
  const [connectingStoreId, setConnectingStoreId] = useState<string | null>(
    null,
  );
  const [externalConnecting, setExternalConnecting] = useState(false);
  const [syncingStoreId, setSyncingStoreId] = useState<string | null>(null);
  const [showAllProviders, setShowAllProviders] = useState(false);
  const [initialOAuthResult] = useState(readOAuthResult);
  const [successConnection, setSuccessConnection] = useState<{
    provider: string;
    storeId: string | null;
  } | null>(() =>
    initialOAuthResult?.value === "connected"
      ? {
          provider: initialOAuthResult.provider,
          storeId: initialOAuthResult.storeId,
        }
      : null,
  );

  useEffect(() => {
    if (!initialOAuthResult) return;

    const providerName =
      getStoreProvider(initialOAuthResult.provider)?.name ??
      initialOAuthResult.provider;
    if (initialOAuthResult.value === "connected") {
      void queryClient.invalidateQueries({
        queryKey: STORE_INTEGRATIONS_QUERY_KEY,
      });
    } else if (initialOAuthResult.value === "error") {
      push({
        variant: "error",
        title: pickT(
          realStoreIntegrationsI18n,
          useLocaleStore.getState().locale,
        ).toasts.connectionFailed(providerName),
        description: initialOAuthResult.reason ?? undefined,
        duration_ms: 6000,
      });
    }

    const params = new URLSearchParams(window.location.search);
    for (const key of [
      "etsy",
      "shopify",
      "amazon",
      "ebay",
      "reason",
      "store",
    ]) {
      params.delete(key);
    }
    const query = params.toString();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}`,
    );
  }, [initialOAuthResult, push, queryClient]);

  const me = useQuery({ queryKey: ["me"], queryFn: fetchMe });
  const orgId = me.data?.active_membership?.org_id ?? null;
  const stores = useQuery({
    queryKey: STORE_INTEGRATIONS_QUERY_KEY,
    queryFn: fetchStoreIntegrations,
  });

  async function startEtsyOAuth(storeIntegrationId: string) {
    const response = await fetch(
      `${API_BASE}/store-integrations/etsy/oauth/authorize-url`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          org_id: orgId,
          store_integration_id: storeIntegrationId,
          redirect_uri: ETSY_REDIRECT_URI,
          scope: ETSY_SCOPE,
        }),
      },
    );
    if (!response.ok) throw new Error(`authorize-url ${response.status}`);
    const body = (await response.json()) as { authorize_url: string };
    completeSellerTourBeforeOAuth(
      sellerTour?.completeActivation,
      body.authorize_url,
    );
  }

  async function connectEtsy(store: StoreRow) {
    if (!orgId) return;
    setConnectingStoreId(store.id);
    try {
      await startEtsyOAuth(store.id);
    } catch (error) {
      setConnectingStoreId(null);
      push({
        variant: "error",
        title: s.toasts.etsyConnectFailed,
        description: String(error),
      });
    }
  }

  // One-click Etsy connect: the browser-redirect callback is reconnect-only,
  // so a store row must exist before the authorize URL is issued. Reuse an
  // abandoned unconnected row if one exists; otherwise create a placeholder —
  // the callback overwrites shop_name/external_shop_id with the real Etsy
  // shop, so the seller never types either.
  async function connectEtsyNew() {
    if (!orgId) return;
    setExternalConnecting(true);
    try {
      let storeId = (stores.data?.items ?? []).find(
        (store) =>
          store.provider === "etsy" &&
          !store.has_access_token &&
          !store.mockData,
      )?.id;
      if (!storeId) {
        const response = await fetch(`${API_BASE}/store-integrations`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            org_id: orgId,
            provider: "etsy",
            external_shop_id: `pending-${crypto.randomUUID()}`,
            shop_name: "Etsy store (connecting…)",
          }),
        });
        if (!response.ok) throw new Error(`create ${response.status}`);
        const created = (await response.json()) as { id: string };
        storeId = created.id;
      }
      await startEtsyOAuth(storeId);
    } catch (error) {
      setExternalConnecting(false);
      push({
        variant: "error",
        title: s.toasts.etsyConnectFailed,
        description: String(error),
      });
    }
  }

  async function connectExternal(
    provider: "shopify" | "amazon" | "ebay",
    shopDomainOverride?: string,
  ) {
    if (!orgId) return;
    setExternalConnecting(true);
    try {
      const path = `/store-integrations/oauth/${provider}/authorize`;
      const payload =
        provider === "shopify"
          ? {
              org_id: orgId,
              shop_domain: (shopDomainOverride ?? shopDomain).trim(),
              online: false,
            }
          : provider === "amazon"
            ? {
                org_id: orgId,
                region: amazonRegion,
                redirect_uri: AMAZON_REDIRECT_URI,
              }
            : { org_id: orgId };
      const response = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`authorize ${response.status}`);
      const body = (await response.json()) as { authorize_url: string };
      completeSellerTourBeforeOAuth(
        sellerTour?.completeActivation,
        body.authorize_url,
      );
    } catch (error) {
      setExternalConnecting(false);
      push({
        variant: "error",
        title: s.toasts.providerConnectFailed(provider),
        description: String(error),
      });
    }
  }

  // Reconnect an already-connected store: re-run its channel OAuth so the
  // merchant re-consents (e.g. to a newly added scope like write_fulfillments)
  // and a fresh token lands on the SAME store row. Shopify keys on the shop
  // domain (external_shop_id == the myshopify domain); Etsy re-runs OAuth for
  // the existing store id. eBay/Amazon re-authorize by org/region.
  async function reconnectStore(store: StoreRow) {
    const provider = store.provider;
    if (provider === "etsy") {
      await startEtsyOAuth(store.id);
      return;
    }
    if (provider === "shopify") {
      await connectExternal("shopify", store.external_shop_id);
      return;
    }
    if (provider === "amazon" || provider === "ebay") {
      await connectExternal(provider);
      return;
    }
    push({
      variant: "error",
      title: t.actions.reconnectUnavailable,
      duration_ms: 5000,
    });
  }

  async function setBackfillWindow(store: StoreRow, window: string) {
    const response = await fetch(`${API_BASE}/store-integrations/${store.id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ order_backfill_window: window }),
    });
    if (response.ok) {
      void queryClient.invalidateQueries({
        queryKey: STORE_INTEGRATIONS_QUERY_KEY,
      });
    } else {
      push({ variant: "error", title: s.toasts.syncWindowFailed });
    }
  }

  async function syncHistory(store: StoreRow) {
    setSyncingStoreId(store.id);
    try {
      const response = await fetch(
        `${API_BASE}/store-integrations/${store.id}/backfill`,
        { method: "POST", headers: { "content-type": "application/json" } },
      );
      if (!response.ok) throw new Error(`backfill ${response.status}`);
      push({
        variant: "success",
        title: s.toasts.historySyncStarted,
        duration_ms: 5000,
      });
    } catch (error) {
      push({
        variant: "error",
        title: s.toasts.historySyncFailed,
        description: String(error),
      });
    } finally {
      setSyncingStoreId(null);
    }
  }

  const connectSectionRef = useRef<HTMLElement>(null);
  const allStores = stores.data?.items ?? [];
  const storeList = allStores.filter((store) => {
    const provider = getStoreProvider(store.provider);
    if (!provider || provider.availability !== "connectable") return false;
    if (!store.mockData || !store.seller_org_id) return true;
    return (
      store.seller_org_id === "org_sarah_thoughtful" ||
      store.seller_org_id === "bo2_sarah"
    );
  });
  const storeGroups = CONNECTABLE_STORE_PROVIDERS.map((provider) => {
    const providerStores = storeList
      .filter((store) => store.provider === provider.id)
      .sort((left, right) => {
        const leftNeedsAttention =
          !left.has_access_token || !left.order_intake_enabled;
        const rightNeedsAttention =
          !right.has_access_token || !right.order_intake_enabled;
        if (leftNeedsAttention !== rightNeedsAttention) {
          return leftNeedsAttention ? -1 : 1;
        }
        return left.shop_name.localeCompare(right.shop_name, undefined, {
          sensitivity: "base",
        });
      });
    return {
      provider,
      stores: providerStores,
      attentionCount: providerStores.filter(
        (store) => !store.has_access_token || !store.order_intake_enabled,
      ).length,
    };
  }).filter((group) => group.stores.length > 0);
  const visibleComingSoon = showAllProviders
    ? COMING_SOON_STORE_PROVIDERS
    : COMING_SOON_STORE_PROVIDERS.slice(0, 3);
  const successStore = successConnection?.storeId
    ? storeList.find((store) => store.id === successConnection.storeId)
    : undefined;
  const successProvider = successConnection
    ? getStoreProvider(successConnection.provider)
    : undefined;

  function selectProvider(provider: StoreProviderDefinition) {
    if (
      provider.id === "etsy" ||
      provider.id === "shopify" ||
      provider.id === "amazon" ||
      provider.id === "ebay"
    ) {
      setActiveForm(provider.id);
    }
  }

  return (
    <div className="mx-auto max-w-7xl space-y-9 pb-10">
      <header
        className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-5"
        data-tour={stores.isSuccess ? "seller-stores-page" : undefined}
      >
        <h1 className="text-3xl font-semibold tracking-tight text-slate-950 appearance-dark:text-[var(--color-text)]">
          {storeList.length > 0 ? t.page.connectedTitle : t.page.emptyTitle}
        </h1>
        {storeList.length > 0 && !stores.isError && canConnect ? (
          <Button
            variant="primary"
            onClick={() =>
              connectSectionRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              })
            }
          >
            {t.page.anotherStore}
          </Button>
        ) : null}
      </header>

      {stores.isLoading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-sm text-slate-500">
          {t.loading}
        </div>
      ) : stores.isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6">
          <p className="text-sm font-medium text-red-800">{t.loadError}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => void stores.refetch()}
          >
            {t.retry}
          </Button>
        </div>
      ) : storeList.length > 0 ? (
        <section
          aria-label={t.page.connectedTitle}
          className="space-y-3"
          data-tour="seller-connected-stores"
        >
          {storeGroups.map((group) => (
            <ConnectedStoreGroup
              key={group.provider.id}
              provider={group.provider}
              storeCount={group.stores.length}
              attentionCount={group.attentionCount}
              copy={t}
            >
              {group.stores.map((store) => (
                <ConnectedStoreItem
                  key={store.id}
                  store={store}
                  provider={group.provider}
                  copy={t}
                  connecting={connectingStoreId === store.id}
                  syncing={syncingStoreId === store.id}
                  onConnectEtsy={(row) => void connectEtsy(row)}
                  onBackfillWindow={(row, window) =>
                    void setBackfillWindow(row, window)
                  }
                  onBackfill={(row) => void syncHistory(row)}
                  onListProduct={() => void navigate({ to: "/seller/catalog" })}
                  onViewStore={() =>
                    void navigate({
                      to: "/seller/stores/$storeId",
                      params: { storeId: store.id },
                    })
                  }
                  onEditStore={() =>
                    void navigate({
                      to: "/seller/settings",
                      search: { section: "store-integrations" },
                    })
                  }
                  onReconnect={(row) => void reconnectStore(row)}
                  canConnect={canConnect}
                  canDelete={canDelete}
                />
              ))}
            </ConnectedStoreGroup>
          ))}
        </section>
      ) : null}

      {!stores.isError && canConnect ? (
        <section
          ref={connectSectionRef}
          aria-labelledby="connect-store-heading"
          data-tour="seller-connect-store"
        >
          <h2
            id="connect-store-heading"
            className="text-xl font-semibold tracking-tight text-slate-950 appearance-dark:text-[var(--color-text)]"
          >
            {storeList.length > 0 ? t.page.anotherStore : t.page.alreadyHave}
          </h2>
          <p className="mt-1 text-sm text-slate-600 appearance-dark:text-[var(--color-text-muted)]">
            {t.page.connectDescription}
          </p>
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            {CONNECTABLE_STORE_PROVIDERS.map((provider) => (
              <StoreProviderCard
                key={provider.id}
                provider={provider}
                copy={t}
                onConnect={selectProvider}
              />
            ))}
          </div>
        </section>
      ) : null}

      {activeForm && canConnect ? (
        <section
          className="space-y-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-5"
          data-tour="seller-connect-form"
        >
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold text-slate-950">
              {s.connectProvider(getStoreProvider(activeForm)?.name ?? "")}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveForm(null)}
            >
              {t.actions.cancel}
            </Button>
          </div>

          {activeForm === "etsy" ? (
            <>
              <p className="max-w-lg text-sm text-slate-600">
                {s.etsyRedirect}
              </p>
              <Button
                variant="primary"
                size="sm"
                data-tour="seller-connect-oauth"
                disabled={externalConnecting}
                onClick={() => void connectEtsyNew()}
              >
                {externalConnecting ? s.connecting : s.connectEtsy}
              </Button>
            </>
          ) : activeForm === "shopify" ? (
            <>
              <label className="block max-w-lg text-xs font-medium text-slate-700">
                {s.shopifyDomain}
                <input
                  value={shopDomain}
                  onChange={(event) => setShopDomain(event.target.value)}
                  placeholder="my-shop.myshopify.com"
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                />
              </label>
              <Button
                variant="primary"
                size="sm"
                data-tour="seller-connect-oauth"
                disabled={externalConnecting || !shopDomain.trim()}
                onClick={() => void connectExternal("shopify")}
              >
                {externalConnecting ? s.connecting : s.connectShopify}
              </Button>
            </>
          ) : activeForm === "ebay" ? (
            <>
              <p className="max-w-lg text-sm text-slate-600">
                {s.ebayRedirect}
              </p>
              <Button
                variant="primary"
                size="sm"
                disabled={externalConnecting}
                onClick={() => void connectExternal("ebay")}
              >
                {externalConnecting ? s.connecting : s.connectEbay}
              </Button>
            </>
          ) : (
            <>
              <label className="block max-w-lg text-xs font-medium text-slate-700">
                {s.amazonRegion}
                <select
                  value={amazonRegion}
                  onChange={(event) => setAmazonRegion(event.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  {AMAZON_REGIONS.map((region) => (
                    <option key={region.value} value={region.value}>
                      {s.amazonRegions[region.value]}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                variant="primary"
                size="sm"
                disabled={externalConnecting}
                onClick={() => void connectExternal("amazon")}
              >
                {externalConnecting ? s.connecting : s.connectAmazon}
              </Button>
            </>
          )}
        </section>
      ) : null}

      {canConnect ? (
        <>
          <section aria-labelledby="more-integrations-heading">
            <h2
              id="more-integrations-heading"
              className="text-sm font-semibold text-slate-800 appearance-dark:text-[var(--color-text)]"
            >
              {t.page.allIntegrations}
            </h2>
            <div className="mt-3 grid gap-3 lg:grid-cols-3">
              {visibleComingSoon.map((provider) => (
                <StoreProviderCard
                  key={provider.id}
                  provider={provider}
                  copy={t}
                  onConnect={selectProvider}
                />
              ))}
            </div>
            <div className="mt-3 flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllProviders((current) => !current)}
              >
                {showAllProviders ? t.actions.showLess : t.actions.showMore}
                <PexIcon
                  name="caretDown"
                  size="xs"
                  tone="muted"
                  className={`ml-1 transition-transform ${
                    showAllProviders ? "rotate-180" : ""
                  }`}
                />
              </Button>
            </div>
          </section>

          <PlatformComparisonSection
            providers={CONNECTABLE_STORE_PROVIDERS.filter(
              (provider) => provider.comparison,
            )}
            copy={t}
            onConnect={selectProvider}
          />
        </>
      ) : null}

      <section className="rounded-2xl border border-indigo-100 appearance-dark:border-[var(--color-border)] bg-gradient-to-r from-indigo-50 via-white to-violet-50 appearance-dark:bg-none appearance-dark:bg-[var(--color-surface)] p-6 sm:p-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-2xl">
            <h2 className="text-xl font-semibold tracking-tight text-slate-950 appearance-dark:text-[var(--color-text)]">
              {t.page.migrationTitle}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600 appearance-dark:text-[var(--color-text-muted)]">
              {t.page.migrationDescription}
            </p>
          </div>
          <Button
            variant="primary"
            className="shrink-0 rounded-full"
            onClick={() => void navigate({ to: "/seller/resources" })}
          >
            {t.actions.contactSupport}
          </Button>
        </div>
      </section>

      <StoreConnectedCelebration
        open={Boolean(successConnection)}
        provider={successProvider ?? null}
        storeName={successStore?.shop_name ?? null}
        onClose={() => setSuccessConnection(null)}
        onListFirstProduct={() => {
          setSuccessConnection(null);
          void navigate({ to: "/seller/catalog" });
        }}
      />
    </div>
  );
}
